import {
	acceptInvitation,
	acceptInvitationById,
	declineInvitation,
} from "@packages/api-client/client/staffController";
import { getMyOrganizationsQueryOptions } from "@packages/api-client/hooks/organizationController";
import {
	getStaffMemberQueryKey,
	listInvitationsQueryKey,
	listMyInvitationsQueryKey,
	listMyInvitationsQueryOptions,
	listStaffAndInvitationsQueryKey,
	listStaffQueryKey,
	useInviteStaffMember as useInviteStaffMemberBase,
	useRemoveStaffMember as useRemoveStaffMemberBase,
	useRevokeInvitation as useRevokeInvitationBase,
	useUpdateStaffMember as useUpdateStaffMemberBase,
	useUpdateStaffRole as useUpdateStaffRoleBase,
} from "@packages/api-client/hooks/staffController";
import type {
	AcceptInvitationMutationResponse,
	ListMyInvitations200,
} from "@packages/api-client/types/staffController";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

export type MyInvitation = ListMyInvitations200[number];

/**
 * Always revalidated on visit: the picker is where an invitation is acted on,
 * and the app default (60s stale + persisted cache) would keep showing an
 * invite that was just declined elsewhere — or hide one that just arrived.
 */
export const myInvitationsQueryOptions = () => ({
	...listMyInvitationsQueryOptions(),
	staleTime: 0,
});

/**
 * Warm the organization list so the org route's loader can place the user in
 * the organization they just joined — see `useAcceptInvitation` below for why
 * this is a `fetchQuery` and why its failure is swallowed.
 */
const refreshMyOrganizations = (
	queryClient: ReturnType<typeof useQueryClient>,
) =>
	queryClient
		.fetchQuery({ ...getMyOrganizationsQueryOptions(), staleTime: 0 })
		.catch(() => undefined);

// ============================================
// Mutation Hooks
// ============================================

export const useInviteStaffMember = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useInviteStaffMemberBase({
		mutation: {
			meta: {
				toastMessages: {
					loading: t("staff.toasts.inviting"),
					success: t("staff.toasts.inviteSuccess"),
				},
			},
			onSuccess: (_, variables) => {
				queryClient.invalidateQueries({
					queryKey: listStaffQueryKey(variables.organizationId),
				});
				queryClient.invalidateQueries({
					queryKey: listInvitationsQueryKey(variables.organizationId),
				});
				queryClient.invalidateQueries({
					queryKey: listStaffAndInvitationsQueryKey(variables.organizationId),
				});
			},
		},
	});
};

export const useUpdateStaffMember = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useUpdateStaffMemberBase({
		mutation: {
			meta: {
				toastMessages: {
					loading: t("staff.toasts.updating"),
					success: t("staff.form.sections.information.updateSuccess"),
				},
			},
			onSuccess: (_, variables) => {
				queryClient.invalidateQueries({
					queryKey: listStaffQueryKey(variables.organizationId),
				});
				queryClient.invalidateQueries({
					queryKey: listStaffAndInvitationsQueryKey(variables.organizationId),
				});
				queryClient.invalidateQueries({
					queryKey: getStaffMemberQueryKey(
						variables.organizationId,
						variables.staffId,
					),
				});
			},
		},
	});
};

export const useUpdateStaffMemberRole = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useUpdateStaffRoleBase({
		mutation: {
			meta: {
				toastMessages: {
					loading: t("staff.toasts.updating"),
					success: t("staff.toasts.updateSuccess"),
				},
			},
			onSuccess: (_, variables) => {
				queryClient.invalidateQueries({
					queryKey: listStaffQueryKey(variables.organizationId),
				});
				queryClient.invalidateQueries({
					queryKey: listStaffAndInvitationsQueryKey(variables.organizationId),
				});
				queryClient.invalidateQueries({
					queryKey: getStaffMemberQueryKey(
						variables.organizationId,
						variables.staffId,
					),
				});
			},
		},
	});
};

export const useRemoveStaffMember = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useRemoveStaffMemberBase({
		mutation: {
			meta: {
				toastMessages: {
					loading: t("staff.toasts.removing"),
					success: t("staff.toasts.removeSuccess"),
				},
			},
			onSuccess: (_, variables) => {
				queryClient.invalidateQueries({
					queryKey: listStaffQueryKey(variables.organizationId),
				});
				queryClient.invalidateQueries({
					queryKey: listStaffAndInvitationsQueryKey(variables.organizationId),
				});
			},
		},
	});
};

export const useRevokeInvitation = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useRevokeInvitationBase({
		mutation: {
			meta: {
				toastMessages: {
					loading: t("staff.toasts.revoking"),
					success: t("staff.toasts.revokeSuccess"),
				},
			},
			onSuccess: (_, variables) => {
				queryClient.invalidateQueries({
					queryKey: listInvitationsQueryKey(variables.organizationId),
				});
				queryClient.invalidateQueries({
					queryKey: listStaffAndInvitationsQueryKey(variables.organizationId),
				});
			},
		},
	});
};

export const useAcceptInvitation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ invitationToken }: { invitationToken: string }) =>
			acceptInvitation(
				invitationToken,
			) as Promise<AcceptInvitationMutationResponse>,
		/**
		 * Accepting an invitation adds an organization to this user's list, and
		 * the accept page routes straight into it (plan §17 phase 6 item 4).
		 *
		 * `fetchQuery`, not `invalidateQueries`: the org route's loader reads the
		 * list with `staleTime: Infinity` and `ensureQueryData` returns cached
		 * data without revalidating, so a merely-invalidated list would still be
		 * the pre-invitation one and org home would render its "not found" arm on
		 * the organization the user just joined. Awaiting the fetch here means the
		 * mutation only reports success once the list can actually place them.
		 *
		 * The refresh is best-effort by design: the membership already exists on
		 * the server, so a failed cache warm must not turn an accepted invitation
		 * into an error screen. The accept page's own fallback (organization
		 * selection) covers that case.
		 */
		onSuccess: () => refreshMyOrganizations(queryClient),
	});
};

/**
 * Accept an invitation listed on the organization picker (by id — the token
 * stays server-side on this path). Same post-accept cache warm as the token
 * flow, plus the picker's own invitation list drops the accepted row.
 */
export const useAcceptInvitationById = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutation({
		mutationFn: ({ invitationId }: { invitationId: string }) =>
			acceptInvitationById(invitationId),
		meta: {
			toastMessages: {
				loading: t("selectOrganization.invitation.accepting"),
				success: t("selectOrganization.invitation.acceptSuccess"),
			},
		},
		onSuccess: async () => {
			queryClient.invalidateQueries({ queryKey: listMyInvitationsQueryKey() });
			await refreshMyOrganizations(queryClient);
		},
	});
};

export const useDeclineInvitation = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutation({
		mutationFn: ({ invitationId }: { invitationId: string }) =>
			declineInvitation(invitationId),
		meta: {
			toastMessages: {
				loading: t("selectOrganization.invitation.declining"),
				success: t("selectOrganization.invitation.declineSuccess"),
			},
		},
		onSuccess: () =>
			queryClient.invalidateQueries({ queryKey: listMyInvitationsQueryKey() }),
	});
};
